import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { drName, formatDateTimeParts, statusLabel } from "@/lib/utils";
import { useApp } from "@/context/AppContext";

// SEC-005: no more email query-based lookup. Requires patient magic-link session
// (or a signed one-off link, handled at /paciente/dashboard).
export default function ManageBooking() {
  const { lang } = useApp();
  const nav = useNavigate();
  const [apts, setApts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDate, setNewDate] = useState({});
  const [newTime, setNewTime] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("agendik_token");
    const role = localStorage.getItem("agendik_role");
    if (!token || role !== "patient") {
      nav("/paciente/login");
      return;
    }
    api.get("/patient/appointments")
      .then(r => setApts(r.data))
      .catch(() => nav("/paciente/login"))
      .finally(() => setLoading(false));
  }, [nav]);

  const cancel = async (id) => {
    try {
      await api.post(`/patient/appointments/${id}/cancel`);
      toast.success("Cita cancelada");
      setApts(apts.map(a => a.id === id ? {...a, status: "cancelled"} : a));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const reschedule = async (id) => {
    try {
      await api.post(`/appointments/${id}/reschedule-patient`, { date: newDate[id], time: newTime[id] });
      toast.success("Reagendada");
      const r = await api.get("/patient/appointments");
      setApts(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (loading) return <div className="max-w-lg mx-auto p-8 text-slate-500">Cargando…</div>;

  return (
    <div className="max-w-lg mx-auto px-4 py-12">
      <Card><CardContent className="p-6 sm:p-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Gestionar mis citas</h1>
        {apts.length === 0 && (
          <p data-testid="no-apts" className="text-sm text-slate-500">No tienes citas activas.</p>
        )}
        <div className="space-y-3">
          {apts.map(apt => (
            <div key={apt.id} data-testid={`apt-${apt.id}`} className="p-4 rounded-xl border border-slate-200 bg-slate-50">
              <div className="font-semibold">{drName(apt.doctor_name)}</div>
              <div className="text-sm text-slate-600">{formatDateTimeParts(apt.date, apt.time)}</div>
              <div className="text-xs text-slate-500 mt-1">Estado: {statusLabel(apt.status, lang)}</div>
              {apt.status !== "cancelled" && (
                <>
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <Input data-testid={`resched-date-${apt.id}`} type="date"
                           value={newDate[apt.id] || ""} onChange={e=>setNewDate({...newDate, [apt.id]: e.target.value})}/>
                    <Input data-testid={`resched-time-${apt.id}`} type="time"
                           value={newTime[apt.id] || ""} onChange={e=>setNewTime({...newTime, [apt.id]: e.target.value})}/>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button data-testid={`resched-btn-${apt.id}`} size="sm" onClick={() => reschedule(apt.id)} className="bg-blue-600 hover:bg-blue-700 flex-1">Reagendar</Button>
                    <Button data-testid={`cancel-btn-${apt.id}`} size="sm" variant="outline" onClick={() => cancel(apt.id)} className="border-red-200 text-red-600 hover:bg-red-50 flex-1">Cancelar</Button>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">Puedes cancelar o reagendar hasta 24 h antes.</p>
                </>
              )}
            </div>
          ))}
        </div>
      </CardContent></Card>
    </div>
  );
}
